import React, { useState, useEffect, useCallback } from 'react';
import { fetchTradersData, createTrader, updateTrader, createTraderBalanceRequest } from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import TradersTable from '../../components/TradersTable';
import AddTraderModal from '../../components/AddTraderModal';
import AddBalanceRequestModal from '../../components/AddBalanceRequestModal';
import SearchBar from '../../components/SearchBar';
import Tabs from '../../components/Tabs';
import useUrlPagination from '../../hooks/useUrlPagination';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useAuth } from '../../contexts/AuthContext';
import '../Page.css';

const Traders = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.isAdmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTrader, setEditingTrader] = useState(null);
  const [balanceTrader, setBalanceTrader] = useState(null);
  const [balanceRequestLoadingRef, setBalanceRequestLoadingRef] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const [activeRole, setActiveRole] = useState('all');
  const [pagination, setPagination] = useState(null);

  const roleTabs = [
    { id: 'all', label: 'All' },
    { id: 'trader', label: 'Trader' },
    { id: 'gateway', label: 'Gateway' },
    { id: 'support', label: 'Support' },
    { id: 'admin', label: 'Admin' }
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const isAllRoles = activeRole === 'all';
      const result = await fetchTradersData(
        isAllRoles ? 1 : currentPage,
        isAllRoles ? 10000 : itemsPerPage,
        debouncedSearchTerm,
        activeRole
      );
      setData(result.data || result);
      setPagination(isAllRoles ? null : (result.pagination || null));
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [activeRole, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleRoleChange = (role) => {
    setActiveRole(role);
    setCurrentPage(1);
  };

  const reload = () => loadData();

  const visibleData = Array.isArray(data) ? data : [];
  const tradersCount = pagination?.total ?? visibleData.length;
  const accountRoleOrder = ['trader', 'gateway', 'support', 'admin'];
  const accountsByRole = accountRoleOrder.map(role => ({
    role,
    accounts: visibleData.filter(account =>
      String(account.role || account.Role || account.ROLE || 'trader').toLowerCase() === role
    )
  }));

  const handleAddTrader = async (traderData) => {
    try {
      const result = await createTrader(traderData);
      if (result.success) {
        reload();
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Failed to create trader' };
      }
    } catch (err) {
      console.error('Error creating trader:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const handleEditTrader = async (traderData, originalTrader) => {
    try {
      // Get trader ref for update
      const traderRef = originalTrader?.ref;
      
      if (!traderRef) {
        return { success: false, error: 'Trader ref not found' };
      }

      // Get all original trader data (now at top level)
      const originalData = originalTrader;
      
      // Create a copy of original data (preserve original keys)
      const mergedData = { ...originalData };
      
      // Update only values of existing fields, preserving original keys
      // Map form fields to possible original key variants
      const fieldMapping = {
        login: ['login', 'Login', 'LOGIN'],
        password: ['password', 'Password', 'PASSWORD'],
        name: ['name', 'Name', 'NAME'],
        role: ['role', 'Role', 'ROLE'],
        status: ['status', 'Status', 'STATUS']
      };
      
      // For each form field, find corresponding original key and update value
      Object.keys(fieldMapping).forEach(formField => {
        const possibleKeys = fieldMapping[formField];
        const newValue = traderData[formField];
        
        // Special handling for password - only update if provided
        if (formField === 'password' && !newValue) {
          return; // Don't update password if empty
        }
        
        // Search for original key in data
        for (const key of possibleKeys) {
          if (key in originalData) {
            // Found original key - update its value
            mergedData[key] = newValue || originalData[key];
            break;
          }
        }
      });

      const result = await updateTrader(traderData, originalTrader);
      if (result.success) {
        reload();
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Failed to update trader' };
      }
    } catch (err) {
      console.error('Error updating trader:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const handleBalanceRequest = async ({ amount, trader }) => {
    const traderId = trader.traderId || trader.TraderId || trader.TRADER_ID || trader.ref;
    const traderRef = trader.ref || traderId;
    const traderLogin = trader.login || trader.Login || trader.LOGIN || '';
    const traderName = trader.name || trader.Name || trader.NAME || '';

    if (!traderId) {
      return { success: false, error: 'Trader ID not found' };
    }

    setBalanceRequestLoadingRef(traderRef);

    try {
      const result = await createTraderBalanceRequest({
        traderId,
        traderRef,
        traderLogin,
        traderName,
        amount
      });

      if (result.status === true || result.success) {
        reload();
        return { success: true };
      }

      return {
        success: false,
        error:
          result.error ||
          result.msg ||
          (result && typeof result === 'object' ? JSON.stringify(result) : null) ||
          'Failed to create balance request'
      };
    } catch (err) {
      console.error('Error creating balance request:', err);
      return { success: false, error: err.message || 'An error occurred' };
    } finally {
      setBalanceRequestLoadingRef('');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Traders</h1>
          <p>Manage accounts, roles, coefficients, and working status in the same operational layout.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Accounts</div>
            <div className="overview-value">{tradersCount}</div>
            <div className="overview-meta">Visible traders</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Control</div>
            <div className="overview-value">Admin</div>
            <div className="overview-meta">Role and status management</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>Trader Directory</h2>
                  <p>Create new accounts, search the list, or open a trader to edit it.</p>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">Admin view</div>
                  <div className="page-chip">{searchTerm ? 'Filtered directory' : 'Live directory'}</div>
                </div>
              </div>
              <Tabs tabs={roleTabs} activeTab={activeRole} onTabChange={handleRoleChange} />
              <SearchBar 
                onSearch={handleSearch}
                value={searchTerm}
                resultCount={pagination?.total || 0}
                isUpdating={searchTerm !== debouncedSearchTerm}
                placeholder="Search traders..."
              />
              {activeRole === 'all' ? (
                <div className="accounts-role-tables">
                  {accountsByRole.map(({ role, accounts }, index) => (
                    <TradersTable
                      key={role}
                      data={accounts}
                      activeRole={role}
                      onAddClick={canManage && index === 0 ? () => setIsAddModalOpen(true) : undefined}
                      onEdit={canManage ? (trader) => setEditingTrader(trader) : undefined}
                      onAddBalance={canManage ? (trader) => setBalanceTrader(trader) : undefined}
                      balanceRequestLoadingRef={balanceRequestLoadingRef}
                    />
                  ))}
                </div>
              ) : (
                <TradersTable
                  data={visibleData}
                  activeRole={activeRole}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onAddClick={canManage ? () => setIsAddModalOpen(true) : undefined}
                  onEdit={canManage ? (trader) => setEditingTrader(trader) : undefined}
                  onAddBalance={canManage ? (trader) => setBalanceTrader(trader) : undefined}
                  balanceRequestLoadingRef={balanceRequestLoadingRef}
                />
              )}
              <AddTraderModal
                isOpen={isAddModalOpen || !!editingTrader}
                onClose={() => {
                  setIsAddModalOpen(false);
                  setEditingTrader(null);
                }}
                onSubmit={editingTrader ? handleEditTrader : handleAddTrader}
                initialData={editingTrader}
                isEdit={!!editingTrader}
              />
              <AddBalanceRequestModal
                isOpen={!!balanceTrader}
                trader={balanceTrader}
                onClose={() => setBalanceTrader(null)}
                onSubmit={handleBalanceRequest}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
      </div>
    </div>
  );
};

export default Traders;
